// Job post-mortem (CFO plan 4.5). Pure. A linked project set to complete opens it; the books work out the job
// (opsJobPostMortem: bid vs actual per bid line and category, people, each line's rate, proposals) and this file
// adds what the phone knows on its own — estimate vs actual per task — plus the note starter and the apply commands.
//
// Proposals are applied only on the owner's tap:
//   template rows  → `taskTemplates/{id}.learned` here (applyTemplateProposals, one group with the project stamp)
//   catalog rows   → BattleBids applyProductionRates (gated); then markCatalogApplied stamps the project
// The project remembers what was applied (`postMortem: { templates: { id: at }, catalog: { id: at } }`) so the next
// look shows those rows as done and the same job is never counted twice.
import { update } from './commands/writes.js';
import { makeTaskTemplate } from './templates.js';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round(v * 100) / 100;
export const POST_MORTEM_TAG = 'post-mortem';

/** Does this project get a post-mortem? Linked to a books job. */
export const hasPostMortem = (project) => !!project?.bookProjectId;

/**
 * Estimate vs actual per task (the job's own tasks, no daily buckets): minutes estimated (a parent whose subtasks
 * carry estimates counts through them) against minutes on its timer. Over first. → [{ id, title, estMin, actMin,
 * overMin, overPct, qty, unit, done }]
 */
export function postMortemTasks(tasks = [], projectId) {
  const mine = tasks.filter((t) => t && t.projectId === projectId && !t.bucket && !t.deletedAt);
  const parents = new Set(mine.filter((t) => t.parentId && num(t.estimatedMinutes) > 0).map((t) => t.parentId));
  return mine.map((t) => {
    const estMin = parents.has(t.id) ? 0 : num(t.estimatedMinutes);
    const actMin = Math.round(num(t.timeSpent) / 60000);
    return {
      id: t.id, title: t.title || 'Task', estMin, actMin, overMin: estMin > 0 ? actMin - estMin : null,
      overPct: estMin > 0 ? Math.round(((actMin - estMin) / estMin) * 100) : null,
      qty: num(t.qty) > 0 ? num(t.qty) : null, unit: t.unit || null, done: t.status === 'done',
    };
  }).filter((r) => r.estMin > 0 || r.actMin > 0)
    .sort((a, b) => (b.overMin ?? -Infinity) - (a.overMin ?? -Infinity) || b.actMin - a.actMin);
}

/** The books' report with what this device has applied since (the project's stamp) folded into the rows. */
export function withApplied(report, project) {
  if (!report?.proposals) return report;
  const pm = project?.postMortem || {};
  const mark = (rows, kind) => (rows || []).map((r) => ({ ...r, applied: !!r.applied || !!pm[kind]?.[r.id] }));
  return { ...report, proposals: { templates: mark(report.proposals.templates, 'templates'), catalog: mark(report.proposals.catalog, 'catalog') } };
}

/**
 * Starter text for "what went over and why": the cost lines, tasks and people that ran over, with a blank to fill in
 * after each. `report` = the books' post-mortem (may be null offline); `tasks` = postMortemTasks rows.
 */
export function overNoteText({ project, report = null, tasks = [], names = {} }) {
  const out = [`What went over and why — ${project?.name || 'the job'}`, ''];
  const lines = (report?.lines || []).filter((l) => num(l.actualCost) > num(l.bidCost) && num(l.bidCost) > 0)
    .sort((a, b) => (num(b.actualCost) - num(b.bidCost)) - (num(a.actualCost) - num(a.bidCost))).slice(0, 8);
  if (lines.length) {
    out.push('Cost lines over the bid:');
    for (const l of lines) out.push(`- ${l.name || 'Line'}: bid $${round2(num(l.bidCost))}, actual $${round2(num(l.actualCost))} (+$${round2(num(l.actualCost) - num(l.bidCost))}) — why: `);
    out.push('');
  }
  const over = tasks.filter((t) => t.overMin > 0).slice(0, 8);
  if (over.length) {
    out.push('Tasks over their estimate:');
    for (const t of over) out.push(`- ${t.title}: ${t.estMin} min planned, ${t.actMin} min taken (${t.overPct > 0 ? '+' : ''}${t.overPct}%) — why: `);
    out.push('');
  }
  const people = (report?.people || []).filter((p) => p.uid && num(p.estHours) > 0 && num(p.hours) > num(p.estHours));
  if (people.length) {
    out.push('People over their share of the estimate:');
    for (const p of people) out.push(`- ${names[p.uid] || p.name}: ${p.estHours} h planned, ${p.hours} h clocked — why: `);
    out.push('');
  }
  if (out.length === 2) out.push('Nothing ran over. Anything to remember for the next one?');
  return out.join('\n').trim();
}

const stamp = (project, kind, ids, at) => {
  const pm = project?.postMortem || {};
  return { ...pm, [kind]: { ...(pm[kind] || {}), ...Object.fromEntries(ids.map((id) => [id, at])) }, at };
};

/**
 * Apply ticked template rows: each template's `learned` becomes the proposed { minutesPerUnit, samples } — only when its
 * samples are still what the proposal was worked out from (`fromSamples`); else it's skipped as changed. One group with
 * the project's stamp. ctx = { projects, templates, now }. rows = proposal rows ({ id, proposed, fromSamples }).
 */
export function applyTemplateProposals(ctx, { projectId, rows = [] }) {
  const project = (ctx.projects || []).find((p) => p.id === projectId);
  if (!project) throw new Error(`no project ${projectId}`);
  const at = typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now();
  const writes = []; const applied = []; const skipped = [];
  for (const r of rows) {
    const tpl = (ctx.templates || []).find((t) => t.id === r.id);
    if (!tpl) { skipped.push({ id: r.id, reason: 'gone' }); continue; }
    const cur = makeTaskTemplate(tpl).learned;
    if ((cur?.samples || 0) !== num(r.fromSamples)) { skipped.push({ id: r.id, reason: 'changed' }); continue; }
    const mpu = num(r.proposed?.minutesPerUnit); const samples = Math.round(num(r.proposed?.samples));
    if (!(mpu > 0) || !(samples > num(r.fromSamples))) { skipped.push({ id: r.id, reason: 'invalid' }); continue; }
    writes.push(update(`taskTemplates/${tpl.id}`, { learned: { minutesPerUnit: mpu, samples, updatedAt: at }, updatedAt: at }));
    applied.push(tpl.id);
  }
  if (applied.length) writes.push(update(`projects/${projectId}`, { postMortem: stamp(project, 'templates', applied, at), updatedAt: at }));
  return { writes, applied, skipped };
}

/** After BattleBids applied catalog rows: stamp them on the project. ctx = { projects, now }. */
export function markCatalogApplied(ctx, { projectId, ids = [] }) {
  const project = (ctx.projects || []).find((p) => p.id === projectId);
  if (!project) throw new Error(`no project ${projectId}`);
  if (!ids.length) return { writes: [] };
  const at = typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now();
  return { writes: [update(`projects/${projectId}`, { postMortem: stamp(project, 'catalog', ids, at), updatedAt: at })] };
}

/** The applyProductionRates payload for ticked catalog rows (names + units ride along for the approval card). */
export function productionRateRows(rows = []) {
  return rows.map((r) => ({ catalogItemId: r.id, hoursPerUnit: num(r.proposed?.hoursPerUnit), samples: Math.round(num(r.proposed?.samples)), fromSamples: num(r.fromSamples), name: r.title || null, unit: r.unit || null }));
}
